import { useEffect, useState } from "react";
import { UserCog } from "lucide-react";

// Shown while a Support Console admin is signed in as a user. The label
// cookie is deliberately readable (it carries no authority); the admin's own
// session lives in a separate httpOnly cookie that the exit route hands back.
const LABEL_COOKIE = "app_impersonating_as";

function readImpersonationLabel(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${LABEL_COOKIE}=`));
  if (!match) return null;
  const value = decodeURIComponent(match.slice(LABEL_COOKIE.length + 1));
  return value || null;
}

export function ImpersonationBanner() {
  const [label, setLabel] = useState<string | null>(null);

  useEffect(() => {
    setLabel(readImpersonationLabel());
    // The cookie is set by a server redirect, so re-check on focus to catch
    // sessions started or ended in another tab.
    const onFocus = () => setLabel(readImpersonationLabel());
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  if (!label) return null;

  return (
    <div className="sticky top-0 z-[100] flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-amber-500 px-4 py-1.5 text-center text-sm font-medium text-black">
      <span className="flex items-center gap-1.5">
        <UserCog className="h-4 w-4" />
        Support mode: you are signed in as {label}. Actions are real and spend
        this user's credits.
      </span>
      <a
        href="/api/support/stop-impersonating"
        className="rounded-md bg-black/85 px-2.5 py-1 text-xs font-semibold text-white hover:bg-black"
      >
        Exit to admin
      </a>
    </div>
  );
}
